import { z } from "zod";

export const CERULEO_SELECTED_SCAN_EVENT = "ceruleo:selected-scan";
export const CERULEO_SELECTED_SCAN_PAYLOAD_EVENT = "ceruleo:selected-scan-payload";
export const CERULEO_SELECTED_SCAN_PAYLOAD_PARAMETER =
	"ceruleoSelectedScanPayload";
export const CERULEO_SELECTED_SCAN_URL_PARAMETER = "ceruleoSelectedScanUrl";
export const CERULEO_SELECTED_SCAN_SESSION_ID_PARAMETER =
	"ceruleoSelectedScanSessionId";
export const CERULEO_SELECTED_SCAN_ASSET_TYPE_PARAMETER =
	"ceruleoSelectedScanAssetType";

export const runtimeSelectedScanSchema = z
	.object({
		scanSessionId: z.string().uuid(),
		assetType: z.string().min(1).max(64).nullable(),
		accessUrl: z.string().url(),
		source: z.enum(["bootstrap", "url", "event"]),
	})
	.strict();

export const selectedScanPayloadSchema = z
	.object({
		scanSessionId: z.string().uuid(),
		asset: z
			.object({
				accessUrl: z.string().url(),
				assetType: z.string().min(1).max(64).nullable(),
			})
			.strict(),
		selectedAt: z.string().datetime(),
		source: z.enum(["bootstrap", "url", "event"]),
	})
	.strict();

const embedEnvelopeSchema = z
	.object({
		id: z.string().min(1).max(128),
		protocolVersion: z.string().regex(/^\d+\.\d+$/),
		event: z.string(),
		source: z.enum(["host", "embed"]),
		sentAt: z.string().datetime(),
		payload: z.record(z.unknown()).default({}),
	})
	.strict();

const bootstrapSelectedScanSchema = z
	.object({
		scanSessionId: z.string().uuid(),
		accessUrl: z.string().url(),
		assetType: z.string().min(1).max(64).nullable(),
	})
	.strict();

const bootstrapLikeSchema = z
	.object({
		selectedScan: bootstrapSelectedScanSchema.nullable().optional(),
	})
	.passthrough();

export type RuntimeSelectedScan = z.infer<typeof runtimeSelectedScanSchema>;
export type SelectedScanPayload = z.infer<typeof selectedScanPayloadSchema>;

declare global {
	interface Window {
		__CERULEO_SELECTED_SCAN__?: RuntimeSelectedScan | null;
		__CERULEO_SELECTED_SCAN_PAYLOAD__?: SelectedScanPayload | null;
	}

	interface WindowEventMap {
		[CERULEO_SELECTED_SCAN_EVENT]: CustomEvent<RuntimeSelectedScan | null>;
		[CERULEO_SELECTED_SCAN_PAYLOAD_EVENT]: CustomEvent<SelectedScanPayload | null>;
	}
}

export function resolveRuntimeSelectedScan(args: {
	bootstrap?: unknown;
	envelope?: unknown;
	url?: string | URL | URLSearchParams | null;
}): RuntimeSelectedScan | null {
	return (
		parseSelectedScanFromEnvelope(args.envelope) ??
		parseSelectedScanFromBootstrap(args.bootstrap) ??
		parseSelectedScanFromUrl(args.url)
	);
}

export function parseSelectedScanFromBootstrap(
	bootstrap: unknown,
): RuntimeSelectedScan | null {
	const parsed = bootstrapLikeSchema.safeParse(bootstrap);
	if (!parsed.success || !parsed.data.selectedScan) {
		return null;
	}

	return runtimeSelectedScanSchema.parse({
		scanSessionId: parsed.data.selectedScan.scanSessionId,
		assetType: parsed.data.selectedScan.assetType ?? null,
		accessUrl: parsed.data.selectedScan.accessUrl,
		source: "bootstrap",
	});
}

export function parseSelectedScanFromEnvelope(
	envelope: unknown,
): RuntimeSelectedScan | null {
	const parsed = embedEnvelopeSchema.safeParse(envelope);
	if (!parsed.success || parsed.data.event !== "scan.selected") {
		return null;
	}

	const payload = parsed.data.payload as Record<string, unknown>;
	const asset = isRecord(payload.asset) ? payload.asset : null;
	const scanSessionId =
		typeof payload.scanSessionId === "string" ? payload.scanSessionId : null;
	const accessUrl =
		asset && typeof asset.accessUrl === "string" ? asset.accessUrl : null;
	const assetType =
		asset && typeof asset.assetType === "string" ? asset.assetType : null;

	if (!scanSessionId || !accessUrl) {
		return null;
	}

	return runtimeSelectedScanSchema.parse({
		scanSessionId,
		assetType,
		accessUrl,
		source: "event",
	});
}

export function parseSelectedScanFromUrl(
	input: string | URL | URLSearchParams | null | undefined,
): RuntimeSelectedScan | null {
	if (!input) {
		return null;
	}

	const searchParams = resolveSearchParams(input);
	const scanSessionId = searchParams.get("ceruleoScanSessionId");
	const accessUrl = searchParams.get("ceruleoSelectedScanUrl");
	const assetType = searchParams.get("ceruleoSelectedScanAssetType");

	if (!scanSessionId || !accessUrl) {
		return null;
	}

	return runtimeSelectedScanSchema.parse({
		scanSessionId,
		assetType: assetType && assetType.trim() ? assetType : null,
		accessUrl,
		source: "url",
	});
}

export function publishSelectedScan(selectedScan: RuntimeSelectedScan | null) {
	const payload = buildSelectedScanPayload(selectedScan);

	window.__CERULEO_SELECTED_SCAN__ = selectedScan;
	window.__CERULEO_SELECTED_SCAN_PAYLOAD__ = payload;
	window.dispatchEvent(
		new CustomEvent<RuntimeSelectedScan | null>(CERULEO_SELECTED_SCAN_EVENT, {
			detail: selectedScan,
		}),
	);
	window.dispatchEvent(
		new CustomEvent<SelectedScanPayload | null>(
			CERULEO_SELECTED_SCAN_PAYLOAD_EVENT,
			{
				detail: payload,
			},
		),
	);
}

export function buildSelectedScanPayload(
	selectedScan: RuntimeSelectedScan | null,
	selectedAt: Date = new Date(),
): SelectedScanPayload | null {
	if (!selectedScan) {
		return null;
	}

	return selectedScanPayloadSchema.parse({
		scanSessionId: selectedScan.scanSessionId,
		asset: {
			accessUrl: selectedScan.accessUrl,
			assetType: selectedScan.assetType,
		},
		selectedAt: selectedAt.toISOString(),
		source: selectedScan.source,
	});
}

export function buildSelectedScanParameterValues(
	payload: SelectedScanPayload | null,
): Record<string, string> {
	if (!payload) {
		return {};
	}

	// Only emit the URL parameter as the standard contract — Grasshopper models
	// universally expect `ceruleoSelectedScanUrl`. Emitting parameters that are
	// not declared inputs on the model can cause ShapeDiver to reject the
	// initialParameterValues batch entirely and skip the initial compute.
	return {
		[CERULEO_SELECTED_SCAN_URL_PARAMETER]: payload.asset.accessUrl,
	};
}

function resolveSearchParams(
	input: string | URL | URLSearchParams,
): URLSearchParams {
	if (input instanceof URLSearchParams) {
		return input;
	}

	if (input instanceof URL) {
		return input.searchParams;
	}

	try {
		if (input.startsWith("http://") || input.startsWith("https://")) {
			return new URL(input).searchParams;
		}
	} catch {
		// Fall through to plain query-string parsing.
	}

	const normalized = input.startsWith("?") ? input.slice(1) : input;
	return new URLSearchParams(normalized);
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}
